import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useProjects } from '../../../../hooks/useProjects';
import { useSubscribers } from '../../../../hooks/useSubscribers';
import apiClient from '../../../../hooks/apiClient';
import { formatDate } from '../../../../utils/formatUtils';
import {
  INITIAL_FILTER_STATE,
  STATUS_COLORS,
  STATUS_LABELS,
  PROJECT_STATUSES,
} from '../constants/dashboardConstants';
import { calculateActiveDays } from '../utils/dateUtils';

/**
 * Hook principal para el Dashboard de Avance de Proyectos
 * Siguiendo los patrones establecidos en el proyecto
 */
export const useDashboard = () => {
  // Estados locales
  const [filter, setFilter] = useState(INITIAL_FILTER_STATE);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const {
    data: projects = [],
    isLoading: projectsLoading,
    refetch: refetchProjects,
  } = useProjects();
  const { data: subscribers = [], isLoading: subscribersLoading } = useSubscribers();

  const { data: allProgressData = [], isLoading: progressLoading } = useQuery({
    queryKey: ['projectProgressTracking', 'all'],
    queryFn: async () => {
      const response = await apiClient.get('project_progress_tracking/');
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const { data: allQualityData = [], isLoading: qualityLoading } = useQuery({
    queryKey: ['projectQualityTracking', 'all'],
    queryFn: async () => {
      const response = await apiClient.get('project_quality_tracking/');
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleFilter = useCallback((name, value) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      [name]: value,
    }));
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
      await refetchProjects();
      showSnackbar('Datos actualizados correctamente', 'success');
    } catch (error) {
      console.error('Error al refrescar datos:', error);
      showSnackbar('Error al actualizar los datos', 'error');
    }
  }, [refetchProjects, showSnackbar]);

  const filteredProjects = useMemo(() => {
    let filtered = projects;

    if (filter.search) {
      const searchTerm = filter.search.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          project.name?.toLowerCase().includes(searchTerm) ||
          project.project_number?.toLowerCase().includes(searchTerm) ||
          project.description?.toLowerCase().includes(searchTerm)
      );
    }

    if (filter.status && filter.status !== PROJECT_STATUSES.ALL) {
      filtered = filtered.filter((project) => project.status === filter.status);
    }

    if (filter.uuid_project) {
      filtered = filtered.filter((project) => project.uuid === filter.uuid_project);
    }

    if (filter.subscriber && filter.subscriber !== 'all') {
      filtered = filtered.filter((project) =>
        project.project_subscribers?.some((ps) => ps.uuid_subscriber === filter.subscriber)
      );
    }

    if (filter.startDate && filter.endDate) {
      filtered = filtered.filter((project) => {
        if (!project.start_date) return false;
        const projectDate = new Date(project.start_date);
        const startDate = new Date(filter.startDate);
        const endDate = new Date(filter.endDate);
        return projectDate >= startDate && projectDate <= endDate;
      });
    }

    return filtered;
  }, [projects, filter]);

  const dashboardStats = useMemo(() => {
    const total = filteredProjects.length;
    const completed = filteredProjects.filter((p) => p.status === 'completed').length;
    const inProgress = filteredProjects.filter((p) => p.status === 'in_progress').length;
    const notStarted = filteredProjects.filter((p) => p.status === 'not_started').length;
    const cancelled = filteredProjects.filter((p) => p.status === 'cancelled').length;

    const totalMaxProgress = allProgressData.reduce(
      (sum, p) => sum + (parseFloat(p.max_percentage) || 0),
      0
    );
    const totalCurrentProgress = allProgressData.reduce(
      (sum, p) => sum + (parseFloat(p.current_percentage) || 0),
      0
    );

    const currentAvgProgress =
      totalMaxProgress > 0 ? (totalCurrentProgress / totalMaxProgress) * 100 : 0;

    const totalQualityEntries = allQualityData.length;
    const completedQualityEntries = allQualityData.filter((q) => q.status === 'completed').length;
    const realAvgQuality =
      totalQualityEntries > 0 ? (completedQualityEntries / totalQualityEntries) * 100 : 0;

    return {
      totalProjects: total,
      completedProjects: completed,
      inProgressProjects: inProgress,
      notStartedProjects: notStarted,
      cancelledProjects: cancelled,
      completionRate: total > 0 ? ((completed / total) * 100).toFixed(1) : 0,
      avgProgress: currentAvgProgress.toFixed(1),
      avgQuality: realAvgQuality.toFixed(1),
    };
  }, [filteredProjects, allProgressData, allQualityData]);

  const projectsWithAnalytics = useMemo(() => {
    let projectsWithData = filteredProjects.map((project) => {
      const progressData = allProgressData.filter((p) => p.uuid_project === project.uuid);

      const qualityData = allQualityData.filter((q) => q.uuid_project === project.uuid);

      const totalMaxPercentage = progressData.reduce(
        (sum, p) => sum + (parseFloat(p.max_percentage) || 0),
        0
      );
      const totalCurrentPercentage = progressData.reduce(
        (sum, p) => sum + (parseFloat(p.current_percentage) || 0),
        0
      );

      const currentProgressAvg =
        totalMaxPercentage > 0 ? (totalCurrentPercentage / totalMaxPercentage) * 100 : 0;

      // Calcular estadísticas de calidad reales
      const completedQuality = qualityData.filter((q) => q.status === 'completed').length;
      const failedQuality = qualityData.filter((q) => q.status === 'failed').length;
      const inProgressQuality = qualityData.filter((q) => q.status === 'in_progress').length;

      // Tasa de éxito real (completed / total) * 100
      const qualitySuccessRate =
        qualityData.length > 0 ? (completedQuality / qualityData.length) * 100 : 0;

      return {
        ...project,
        progressMetrics: {
          totalTasks: progressData.length,
          completionPercentage: currentProgressAvg,
          tasksCompleted: progressData.filter((p) => {
            const currentPerc = parseFloat(p.current_percentage) || 0;
            const maxPerc = parseFloat(p.max_percentage) || 0;
            return maxPerc > 0 && Math.abs(currentPerc - maxPerc) < 0.01;
          }).length,
        },
        qualityMetrics: {
          totalControls: qualityData.length,
          completedControls: completedQuality,
          failedControls: failedQuality,
          inProgressControls: inProgressQuality,
          successRate: qualitySuccessRate,
          averageQualityRate: qualitySuccessRate,
          qualityDistribution: {
            low: qualityData.filter((q) => q.severity === 'low').length,
            medium: qualityData.filter((q) => q.severity === 'medium').length,
            high: qualityData.filter((q) => q.severity === 'high').length,
          },
        },
        daysActive: calculateActiveDays(project.start_date, project.end_date),
      };
    });

    if (filter.sortBy) {
      projectsWithData.sort((a, b) => {
        switch (filter.sortBy) {
          case 'name':
            return (a.name || '').localeCompare(b.name || '');
          case 'progress':
            return (
              (b.progressMetrics?.completionPercentage || 0) -
              (a.progressMetrics?.completionPercentage || 0)
            );
          case 'quality':
            return (b.qualityMetrics?.successRate || 0) - (a.qualityMetrics?.successRate || 0);
          case 'start_date':
            const dateA = new Date(a.start_date || '1900-01-01');
            const dateB = new Date(b.start_date || '1900-01-01');
            return dateB - dateA;
          default:
            return 0;
        }
      });
    }

    return projectsWithData;
  }, [filteredProjects, allProgressData, allQualityData, filter.sortBy]);

  const chartData = useMemo(() => {
    const statusChart = [
      {
        name: STATUS_LABELS[PROJECT_STATUSES.COMPLETED],
        value: dashboardStats.completedProjects,
        color: STATUS_COLORS[PROJECT_STATUSES.COMPLETED],
      },
      {
        name: STATUS_LABELS[PROJECT_STATUSES.IN_PROGRESS],
        value: dashboardStats.inProgressProjects,
        color: STATUS_COLORS[PROJECT_STATUSES.IN_PROGRESS],
      },
      {
        name: STATUS_LABELS[PROJECT_STATUSES.NOT_STARTED],
        value: dashboardStats.notStartedProjects,
        color: STATUS_COLORS[PROJECT_STATUSES.NOT_STARTED],
      },
      {
        name: STATUS_LABELS[PROJECT_STATUSES.CANCELLED],
        value: dashboardStats.cancelledProjects,
        color: STATUS_COLORS[PROJECT_STATUSES.CANCELLED],
      },
    ].filter((item) => item.value > 0);

    const timelineChart = generateTimelineData(filteredProjects);

    const subscriberChart = generateSubscriberData(filteredProjects, subscribers);

    return {
      statusChart,
      timelineChart,
      subscriberChart,
    };
  }, [dashboardStats, filteredProjects, subscribers]);

  useEffect(() => {
    if (filter.refreshInterval > 0) {
      const interval = setInterval(handleRefresh, filter.refreshInterval);
      return () => clearInterval(interval);
    }
  }, [filter.refreshInterval, handleRefresh]);

  const isLoading = projectsLoading || subscribersLoading || progressLoading || qualityLoading;

  return {
    // Estados de datos
    data: filteredProjects,
    projects: filteredProjects,
    allProjects: projects,
    projectsWithAnalytics,
    subscribers,
    dashboardStats,
    chartData,
    isLoading,
    filter,

    // Funciones de manejo
    handleFilter,
    handleRefresh,

    // Notificaciones
    snackbar,
    handleCloseSnackbar,
  };
};

const generateTimelineData = (projects) => {
  const monthlyData = {};

  projects.forEach((project) => {
    if (!project.start_date) return;

    const date = new Date(project.start_date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthName = formatDate(date, { options: { year: 'numeric', month: 'short' } });

    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = {
        key: monthKey,
        month: monthName,
        total: 0,
        completed: 0,
        inProgress: 0,
      };
    }

    monthlyData[monthKey].total++;
    if (project.status === PROJECT_STATUSES.COMPLETED) monthlyData[monthKey].completed++;
    if (project.status === PROJECT_STATUSES.IN_PROGRESS) monthlyData[monthKey].inProgress++;
  });

  return Object.values(monthlyData)
    .sort((a, b) => a.key.localeCompare(b.key))
    .map(({ key, ...rest }) => rest)
    .slice(-12); // Últimos 12 meses
};

// Función auxiliar para generar datos por suscriptor
const generateSubscriberData = (projects, subscribers) => {
  const subscriberStats = {};

  projects.forEach((project) => {
    if (!project.project_subscribers) return;

    project.project_subscribers.forEach((ps) => {
      const subscriber = subscribers.find((s) => s.uuid === ps.uuid_subscriber);
      if (!subscriber) return;

      const key = `${subscriber.first_name} ${subscriber.last_name}`;

      if (!subscriberStats[key]) {
        subscriberStats[key] = {
          name: key,
          total: 0,
          completed: 0,
          completionRate: 0,
        };
      }

      subscriberStats[key].total++;
      if (project.status === PROJECT_STATUSES.COMPLETED) {
        subscriberStats[key].completed++;
      }
    });
  });

  // Calcular tasa de completación y ordenar
  return Object.values(subscriberStats)
    .map((stats) => ({
      ...stats,
      completionRate: stats.total > 0 ? ((stats.completed / stats.total) * 100).toFixed(1) : 0,
    }))
    .sort((a, b) => b.completionRate - a.completionRate)
    .slice(0, 10); // Top 10
};
